"use client";

import * as React from "react";
import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from "recharts";

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import type { SeriesChartProps } from "./chart-cartesian";

export type ChartRadarProps = SeriesChartProps & {
	/**
	 * - `default` — filled radar(s) with a polygon grid.
	 * - `grid-circle` — a circular grid instead of a polygon.
	 * - `grid-filled` — a polygon grid with heavier series fill.
	 * - `grid-none` — no grid lines.
	 */
	variant?: "default" | "grid-circle" | "grid-filled" | "grid-none";
};

/**
 * Radar chart built on the shared `ChartContainer` primitives. Palette-agnostic:
 * every series colour is read from `config` as `var(--color-<key>)`.
 */
export function ChartRadar({
	data,
	config,
	series,
	categoryKey,
	variant = "default",
	className,
	valueFormatter,
	hideTooltip = false,
	showLegend,
}: ChartRadarProps) {
	const withLegend = showLegend ?? series.length > 1;
	const fillOpacity = variant === "grid-filled" ? 0.6 : 0.3;

	return (
		<ChartContainer config={config} className={className}>
			<RadarChart data={data}>
				{!hideTooltip && (
					<ChartTooltip
						content={<ChartTooltipContent valueFormatter={valueFormatter} />}
					/>
				)}
				{variant !== "grid-none" && (
					<PolarGrid
						gridType={variant === "grid-circle" ? "circle" : "polygon"}
					/>
				)}
				<PolarAngleAxis dataKey={categoryKey} />
				{withLegend && <ChartLegend content={<ChartLegendContent />} />}
				{series.map((key) => (
					<Radar
						key={key}
						dataKey={key}
						stroke={seriesColor(key)}
						fill={seriesColor(key)}
						fillOpacity={fillOpacity}
					/>
				))}
			</RadarChart>
		</ChartContainer>
	);
}
